import type {
  UnifiedCheckoutBasketAddItemType,
  UnifiedCheckoutBasketType,
  UnifiedCheckoutBasketUpdateType,
} from "@sellar-io/api-v2/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useFirebaseUser } from "../../../useFirebaseUser.shared";
import { useSellarCurrentBusiness } from "../../../useSellarCurrentBusiness.shared";
import {
  apiV2AssertResponse,
  apiV2Request,
  getDefaultQueryOptionsV2,
} from "../apiV2Request";
import { ServerStateKeys } from "../ServerStateKeys";

const useContext = () => {
  const { business } = useSellarCurrentBusiness();
  const { user } = useFirebaseUser();
  return { business, user };
};

export const useUnifiedCheckoutBasketList = () => {
  const { business, user } = useContext();
  return useQuery({
    queryKey: [user?.uid, business?.id, ServerStateKeys.UnifiedCheckoutBaskets],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: "unified-checkout-baskets",
      }).then(
        (response) =>
          apiV2AssertResponse<UnifiedCheckoutBasketType[]>(response).data,
      ),
    ...getDefaultQueryOptionsV2<UnifiedCheckoutBasketType[]>({
      context: { scope: "buyer", user, business },
    }),
  });
};

export const useUnifiedCheckoutBasket = (uuid?: string) => {
  const { business, user } = useContext();
  return useQuery({
    queryKey: [
      user?.uid,
      business?.id,
      ServerStateKeys.UnifiedCheckoutBaskets,
      uuid,
    ],
    queryFn: () =>
      apiV2Request({
        user,
        business,
        method: "get",
        path: `unified-checkout-baskets/${uuid}`,
      }).then(
        (response) =>
          apiV2AssertResponse<UnifiedCheckoutBasketType>(response).data,
      ),
    ...getDefaultQueryOptionsV2<UnifiedCheckoutBasketType>({
      context: { scope: "buyer", user, business },
      options: { enabled: !!uuid },
    }),
  });
};

const useInvalidateBaskets = () => {
  const queryClient = useQueryClient();
  const { business, user } = useContext();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [
        user?.uid,
        business?.id,
        ServerStateKeys.UnifiedCheckoutBaskets,
      ],
      exact: true,
    });
};

export const useUnifiedCheckoutBasketAddItem = () => {
  const { business, user } = useContext();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateBaskets();
  const queryKey = [
    user?.uid,
    business?.id,
    ServerStateKeys.UnifiedCheckoutBaskets,
  ];
  return useMutation({
    mutationFn: (data: UnifiedCheckoutBasketAddItemType) =>
      apiV2Request({
        user,
        business,
        method: "post",
        path: "unified-checkout-baskets/items",
        data,
      }).then(
        (response) =>
          apiV2AssertResponse<UnifiedCheckoutBasketType>(response).data,
      ),
    onSuccess: (basket, input) => {
      queryClient.setQueryData<UnifiedCheckoutBasketType[]>(
        queryKey,
        (baskets = []) => {
          const remainingBaskets = baskets
            .filter((entry) => entry.uuid !== basket.uuid)
            .map((entry) => ({
              ...entry,
              DraftOrders: entry.DraftOrders.map((draft) => ({
                ...draft,
                Items: draft.Items.filter(
                  (item) => item.ProductId !== input.ProductId,
                ),
              })).filter((draft) => draft.Items.length > 0),
            }))
            .filter((entry) => entry.DraftOrders.length > 0);

          return [...remainingBaskets, basket].sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );
        },
      );
      queryClient.setQueryData([...queryKey, basket.uuid], basket);
      void queryClient.invalidateQueries({
        queryKey: [...queryKey, basket.uuid, "products"],
        exact: true,
      });
      void invalidate();
    },
  });
};

export const useUnifiedCheckoutBasketUpdate = (uuid: string) => {
  const { business, user } = useContext();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateBaskets();
  const queryKey = [
    user?.uid,
    business?.id,
    ServerStateKeys.UnifiedCheckoutBaskets,
  ];
  return useMutation({
    mutationFn: (data: UnifiedCheckoutBasketUpdateType) =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `unified-checkout-baskets/${uuid}`,
        data,
      }).then(
        (response) =>
          apiV2AssertResponse<UnifiedCheckoutBasketType>(response).data,
      ),
    onSuccess: (basket) => {
      queryClient.setQueryData([...queryKey, basket.uuid], basket);
      void invalidate();
    },
  });
};

export const useUnifiedCheckoutBasketUpdateItem = () => {
  const { business, user } = useContext();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateBaskets();
  const queryKey = [
    user?.uid,
    business?.id,
    ServerStateKeys.UnifiedCheckoutBaskets,
  ];

  return useMutation({
    mutationFn: ({
      basketUuid,
      itemId,
      quantity,
    }: {
      basketUuid: string;
      itemId: number;
      quantity: number;
    }) =>
      apiV2Request({
        user,
        business,
        method: "put",
        path: `unified-checkout-baskets/${basketUuid}/items/${itemId}`,
        data: { quantity },
      }).then(
        (response) =>
          apiV2AssertResponse<UnifiedCheckoutBasketType | null>(response).data,
      ),
    onSuccess: (basket, { basketUuid, itemId }) => {
      queryClient.setQueryData<UnifiedCheckoutBasketType[]>(
        queryKey,
        (baskets = []) => {
          const remainingBaskets = baskets
            .filter((entry) => entry.uuid !== basket?.uuid)
            .map((entry) => ({
              ...entry,
              DraftOrders: entry.DraftOrders.map((draft) => ({
                ...draft,
                Items: draft.Items.filter((item) => item.id !== itemId),
              })).filter((draft) => draft.Items.length > 0),
            }))
            .filter((entry) => entry.DraftOrders.length > 0);

          return basket ? [...remainingBaskets, basket] : remainingBaskets;
        },
      );
      if (basket) {
        queryClient.setQueryData([...queryKey, basket.uuid], basket);
      } else {
        queryClient.removeQueries({
          queryKey: [...queryKey, basketUuid],
          exact: true,
        });
      }
      void invalidate();
    },
  });
};
